import React from 'react'
import { Link } from 'react-router-dom'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { ArrowRight, SlidersHorizontal } from 'lucide-react'
import PerformanceDashboardService from '../../../../services/dashboards/PerformanceDashboard.service'
// @TODO: Fix the filter in this component
export type SalesAndInventoryView = 'sales' | 'inventory'
export type SalesAndInventoryRange = 'ytd' | '6m' | '12m'

export interface SalesAndInventorySummaryCard {
  label: string;
  value: string;
  sub: string;
  tone: string;
}

export interface SalesAndInventoryChartPoint {
  date: string;
  actual: number;
  plan: number;
  lastYear: number;
}

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path
        d="M10 3L4.5 8.5L2 6"
        stroke="#7F56D9"
        strokeWidth="1.6666"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function ItemCheckbox({ checked }: { checked: boolean }) {
  return (
    <div className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${checked ? 'border-[#7F56D9] bg-[#F9F5FF]' : 'border-[#98A2B3] bg-white'}`}>
      {checked && <CheckIcon />}
    </div>
  )
}

interface SalesAndInventoryPerformanceSectionProps {
  activeView: SalesAndInventoryView;
  onViewChange: (view: SalesAndInventoryView) => void;
  range: SalesAndInventoryRange;
  onRangeChange: (range: SalesAndInventoryRange) => void;
  title: string;
  subtitle: string;
  summaryCards: SalesAndInventorySummaryCard[];
  chartData: SalesAndInventoryChartPoint[];
  emptyMessage: string;
  analysisHref?: string | null;
  onAnalysisClick?: () => void;
  hasFilters?: boolean;
}

const viewTabs: Array<{ value: SalesAndInventoryView; label: string }> = [
  { value: 'sales', label: 'Sales' },
  { value: 'inventory', label: 'Inventory' },
]

const rangeTabs: Array<{ value: SalesAndInventoryRange; label: string }> = [
  { value: 'ytd', label: 'YTD' },
  { value: '6m', label: '6 Months' },
  { value: '12m', label: '12 Months' },
]

const legendItems = [
  { label: 'Actual', color: '#6366F1' },
  { label: 'Plan', color: '#F79009' },
  { label: 'Last year', color: '#7F56D9' },
]

function FiltersAppliedBadge({ hasFilters }: { hasFilters?: boolean }) {
  if (!hasFilters) return null
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#ECFDF5] px-2 py-0.5 text-[11px] font-semibold text-[#027A48]">
      <span className="h-1.5 w-1.5 rounded-full bg-[#12B76A]" />
      Filters applied
    </span>
  )
}

export default function SalesAndInventoryPerformanceSection({
  activeView,
  onViewChange,
  range,
  onRangeChange,
  title,
  subtitle,
  summaryCards,
  chartData,
  emptyMessage,
  analysisHref,
  onAnalysisClick,
  hasFilters,
}: SalesAndInventoryPerformanceSectionProps) {
  const [filtersOpen, setFiltersOpen] = React.useState(false)
  const [draftRange, setDraftRange] = React.useState<SalesAndInventoryRange>(range)
  const filterControlsRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    setDraftRange(range)
  }, [range])

  React.useEffect(() => {
    if (!filtersOpen) return undefined

    const dismissFilters = (event: MouseEvent) => {
      if (!filterControlsRef.current?.contains(event.target as Node)) {
        setDraftRange(range)
        setFiltersOpen(false)
      }
    }

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDraftRange(range)
        setFiltersOpen(false)
      }
    }

    document.addEventListener('mousedown', dismissFilters)
    document.addEventListener('keydown', closeOnEscape)

    return () => {
      document.removeEventListener('mousedown', dismissFilters)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [filtersOpen, range])

  const dismissFilters = React.useCallback(() => {
    setDraftRange(range)
    setFiltersOpen(false)
  }, [range])

  const resetFilters = React.useCallback(() => {
    setDraftRange('6m')
  }, [])

  const applyFilters = React.useCallback(() => {
    onRangeChange(draftRange)
    setFiltersOpen(false)
  }, [draftRange, onRangeChange])

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold tracking-tight text-gray-900">{title}</h2>
            <FiltersAppliedBadge hasFilters={hasFilters} />
          </div>
          <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
        </div>

        <div ref={filterControlsRef} className="relative flex flex-wrap items-start justify-end gap-2 lg:justify-end">
          <div aria-label="Sales and inventory view" className="inline-flex rounded-md border border-[#D5D7DA] bg-[#F3F3F5] p-1">
            {viewTabs.map((tab) => {
              const isSelected = activeView === tab.value
              return (
                <button
                  key={tab.value}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => onViewChange(tab.value)}
                  className={`rounded-[4px] px-3.5 py-1.5 text-[13px] font-medium leading-[150%] transition-colors ${isSelected
                    ? 'bg-white text-[#6941C6] shadow-[0_1px_3px_rgba(0,0,0,0.1)]'
                    : 'text-[#667085] hover:text-[#101828]'}`}
                >
                  {tab.label}
                </button>
              )
            })}
          </div>

          <button
            type="button"
            aria-label="Open sales and inventory filters"
            aria-expanded={filtersOpen}
            aria-haspopup="dialog"
            onClick={() => {
              setDraftRange(range)
              setFiltersOpen((isOpen) => !isOpen)
            }}
            className="inline-flex h-[42px] items-center gap-2 rounded-md border border-[#D0D5DD] bg-white px-3.5 text-sm font-medium text-[#344054] shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-100"
          >
            <SlidersHorizontal aria-hidden="true" className="h-4 w-4" />
            Filters
          </button>

          {filtersOpen && (
            <div
              aria-label="Sales and inventory filters"
              role="dialog"
              className="absolute right-0 top-[52px] z-40 w-[300px] overflow-hidden rounded-lg border border-[#EAECF0] bg-white shadow-[0_12px_16px_-4px_rgba(0,0,0,0.08),0_4px_6px_-2px_rgba(0,0,0,0.03)]"
            >
              <div className="px-4 py-3">
                <span className="text-sm font-semibold text-[#344054]">Filters</span>
              </div>
              <div className="h-px w-full bg-[#F5F5F5]" />

              <div className="flex flex-col">
                {rangeTabs.map((tab) => {
                  const isSelected = draftRange === tab.value
                  return (
                    <button
                      key={tab.value}
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => setDraftRange(tab.value)}
                      className="flex items-center gap-3 px-4 py-2.5 text-left hover:bg-secondary/60"
                    >
                      <ItemCheckbox checked={isSelected} />
                      <span className="text-sm font-medium text-[#344054]">{tab.label}</span>
                    </button>
                  )
                })}
              </div>

              <div className="h-px w-full bg-[#F5F5F5]" />
              <div className="flex items-center justify-between px-4 py-3">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="text-xs font-medium text-[#667085] hover:text-[#101828]"
                >
                  Reset
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={dismissFilters}
                    className="rounded-md border border-[#D0D5DD] bg-white px-3.5 py-2 text-sm font-medium text-[#344054] hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={applyFilters}
                    className="rounded-md bg-[#7F56D9] px-3.5 py-2 text-sm font-semibold text-white hover:bg-[#6941C6]"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
        {summaryCards.map((card, index) => (
          <div key={`${card.label}-${index}`} className="flex flex-col gap-1.5 rounded-[10px] bg-[#F8FAFC] px-3.5 py-3">
            <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#667085]">{card.label}</div>
            <div className={`font-semibold text-[#101828] ${index === 0 ? 'text-2xl' : 'text-lg'}`}>
              {card.value}
            </div>
            <div className={`text-xs ${card.tone}`}>{card.sub}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-4">
        {legendItems.map((item) => (
          <div key={item.label} className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />
            <span className="text-[13px] leading-[150%] text-[#344054]">{item.label}</span>
          </div>
        ))}
      </div>

      <div className="mt-4 h-72 w-full">
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#EAECF0" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: '#667085', fontSize: 12 }} stroke="#667085" interval={0} padding={{ left: 16, right: 16 }} />
              <YAxis hide />
              <Tooltip
                formatter={(value: number) => PerformanceDashboardService.moneyFormat(value || 0, true)}
                contentStyle={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  borderRadius: '12px',
                }}
                cursor={false}
              />
              <Line
                type={chartData.length > 2 ? 'monotone' : 'linear'}
                dataKey="actual"
                stroke="#6366F1"
                strokeWidth={2.5}
                name="Actual"
                connectNulls
                dot={{ r: 3, strokeWidth: 0, fill: '#6366F1' }}
                activeDot={{ r: 4, strokeWidth: 0, fill: '#6366F1' }}
              />
              <Line
                type={chartData.length > 2 ? 'monotone' : 'linear'}
                dataKey="plan"
                stroke="#F79009"
                strokeWidth={2.5}
                name="Plan"
                connectNulls
                dot={{ r: 3, strokeWidth: 0, fill: '#F79009' }}
                activeDot={{ r: 4, strokeWidth: 0, fill: '#F79009' }}
              />
              <Line
                type={chartData.length > 2 ? 'monotone' : 'linear'}
                dataKey="lastYear"
                stroke="#7F56D9"
                strokeWidth={2}
                name="Last year"
                connectNulls
                dot={{ r: 3, strokeWidth: 0, fill: '#7F56D9' }}
                activeDot={{ r: 4, strokeWidth: 0, fill: '#7F56D9' }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-6 text-center text-sm text-gray-500">
            {emptyMessage}
          </div>
        )}
      </div>

      {analysisHref && (
        <div className="mt-4 flex justify-end">
          <Link
            to={analysisHref}
            onClick={onAnalysisClick}
            className="inline-flex items-center gap-2 rounded-[4px] px-4 py-2 text-sm font-medium text-purple-700 no-underline transition-colors hover:bg-purple-50"
          >
            View Full analysis
            <ArrowRight className="h-4 w-4" strokeWidth={1.33} />
          </Link>
        </div>
      )}
    </section>
  )
}

